import React from "react";
import { Typography } from "antd";
import dayjs from "dayjs";

const { Text } = Typography;

const DIFF_KEY_LABELS: Record<string, string> = {
  old_value: "Previous",
  oldValue: "Previous",
  OldValue: "Previous",
  new_value: "Updated",
  newValue: "Updated",
  NewValue: "Updated",
};

const HIDDEN_KEYS = new Set([
  "password",
  "Password",
  "apikey",
  "ApiKey",
  "apiKey",
  "APIKey",
  "secret",
  "Secret",
  "token",
  "Token",
  "accessToken",
  "refreshToken",
  "SmsApiKey",
  "smsApiKey",
]);

const PREFERRED_OBJECT_KEYS = [
  "GroupName",
  "Name",
  "name",
  "Email",
  "email",
  "TelNumber",
  "Phone",
  "AddressLine1",
  "AddressLine2",
  "City",
  "Suburb",
  "PostalCode",
  "Province",
  "Country",
  "id",
  "Id",
  "GroupLinkID",
];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const looksLikeIsoDate = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}(T|\s)/.test(value) || /^\d{4}-\d{2}-\d{2}$/.test(value);

export const formatAuditLabel = (key: string): string => {
  if (DIFF_KEY_LABELS[key]) return DIFF_KEY_LABELS[key];
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

export const tryParseJson = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (
    !(trimmed.startsWith("{") && trimmed.endsWith("}")) &&
    !(trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

export const formatScalarAuditValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);

  if (typeof value === "string") {
    const parsed = tryParseJson(value);
    if (parsed !== value) {
      return formatScalarAuditValue(parsed);
    }
    if (looksLikeIsoDate(value) && dayjs(value).isValid()) {
      return value.includes("T")
        ? dayjs(value).format("YYYY-MM-DD HH:mm")
        : dayjs(value).format("YYYY-MM-DD");
    }
    return value;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return "None";
    return value
      .map((item) =>
        isPlainObject(item)
          ? summarizeEntity(item)
          : formatScalarAuditValue(item)
      )
      .join(", ");
  }

  if (isPlainObject(value)) {
    return summarizeEntity(value);
  }

  return String(value);
};

const summarizeEntity = (obj: Record<string, unknown>): string => {
  const name =
    obj.GroupName ??
    obj.Name ??
    obj.name ??
    ([obj.FirstName, obj.Surname].filter(Boolean).join(" ").trim() || null);
  if (name != null && String(name).trim() !== "") {
    const id = obj.id ?? obj.Id;
    return id != null ? `${name} (ID: ${id})` : String(name);
  }
  const keys = Object.keys(obj).filter((k) => !HIDDEN_KEYS.has(k));
  if (keys.length === 0) return "{}";
  return keys
    .slice(0, 3)
    .map((k) => `${formatAuditLabel(k)}: ${formatScalarAuditValue(obj[k])}`)
    .join(", ");
};

/** Prefer a short readable field list for large entities (e.g. Group dumps). */
const formatCompactObjectNode = (
  obj: Record<string, unknown>
): React.ReactNode => {
  const preferred = PREFERRED_OBJECT_KEYS.filter(
    (key) =>
      Object.prototype.hasOwnProperty.call(obj, key) &&
      obj[key] != null &&
      obj[key] !== "" &&
      !HIDDEN_KEYS.has(key)
  );

  const entries =
    preferred.length > 0
      ? preferred.map((key) => [key, obj[key]] as [string, unknown])
      : sortObjectEntries(
          Object.entries(obj).filter(([key]) => !HIDDEN_KEYS.has(key))
        ).slice(0, 6);

  if (entries.length === 0) return summarizeEntity(obj);

  return (
    <div style={{ paddingLeft: 12 }}>
      {entries.map(([nestedKey, nestedValue]) => (
        <div key={nestedKey} style={{ margin: "4px 0" }}>
          <Text type="secondary">{formatAuditLabel(nestedKey)}: </Text>
          <Text>{formatScalarAuditValue(nestedValue)}</Text>
        </div>
      ))}
    </div>
  );
};

const sortObjectEntries = (
  entries: [string, unknown][]
): [string, unknown][] => {
  const preferred = PREFERRED_OBJECT_KEYS;
  return [...entries].sort(([a], [b]) => {
    const ai = preferred.indexOf(a);
    const bi = preferred.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
};

const isDiffObject = (obj: Record<string, unknown>): boolean => {
  const keys = Object.keys(obj);
  return keys.some(
    (k) =>
      k === "old_value" ||
      k === "new_value" ||
      k === "oldValue" ||
      k === "newValue" ||
      k === "OldValue" ||
      k === "NewValue"
  );
};

interface FormatOptions {
  depth?: number;
  maxDepth?: number;
}

export const formatAuditValueNode = (
  value: unknown,
  options: FormatOptions = {}
): React.ReactNode => {
  const depth = options.depth ?? 0;
  const maxDepth = options.maxDepth ?? 4;

  if (React.isValidElement(value)) return value;
  if (value === null || value === undefined) return "N/A";

  const parsed = tryParseJson(value);

  if (Array.isArray(parsed)) {
    if (parsed.length === 0) return "None";
    if (depth >= maxDepth) return formatScalarAuditValue(parsed);
    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
        {parsed.map((item, index) => (
          <div key={index} style={{ margin: "4px 0" }}>
            <Text type="secondary">{index + 1}. </Text>
            {formatAuditValueNode(item, { depth: depth + 1, maxDepth })}
          </div>
        ))}
      </div>
    );
  }

  if (isPlainObject(parsed)) {
    const entries = Object.entries(parsed).filter(
      ([key]) => !HIDDEN_KEYS.has(key)
    );
    if (entries.length === 0) return "{}";

    // Diff wrappers: show Previous / Updated clearly
    if (isDiffObject(parsed)) {
      const ordered = sortObjectEntries(entries).sort(([a], [b]) => {
        const order = (k: string) =>
          /old/i.test(k) ? 0 : /new/i.test(k) ? 1 : 2;
        return order(a) - order(b);
      });
      return (
        <div style={{ paddingLeft: 12, marginTop: 4 }}>
          {ordered.map(([nestedKey, nestedValue]) => (
            <div key={nestedKey} style={{ margin: "6px 0" }}>
              <Text type="secondary">{formatAuditLabel(nestedKey)}: </Text>
              <div style={{ marginTop: 2 }}>
                {formatAuditValueNode(nestedValue, {
                  depth: depth + 1,
                  maxDepth,
                })}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Deep nesting / large blobs: show preferred human fields only
    if (depth >= maxDepth || entries.length > 12) {
      return formatCompactObjectNode(parsed);
    }

    const ordered = sortObjectEntries(entries);
    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
        {ordered.map(([nestedKey, nestedValue]) => {
          const nestedParsed = tryParseJson(nestedValue);
          const isNestedComplex =
            isPlainObject(nestedParsed) || Array.isArray(nestedParsed);

          return (
            <div key={nestedKey} style={{ margin: "4px 0" }}>
              <Text type="secondary">{formatAuditLabel(nestedKey)}: </Text>
              {isNestedComplex ? (
                <div style={{ marginTop: 2 }}>
                  {formatAuditValueNode(nestedValue, {
                    depth: depth + 1,
                    maxDepth,
                  })}
                </div>
              ) : (
                <Text>{formatScalarAuditValue(nestedValue)}</Text>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return formatScalarAuditValue(parsed);
};
